import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, getAccessToken, setAccessToken } from "@/lib/api";
import { env } from "@/lib/env";

export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  plan: string;
  role: string;
};

type AuthContextValue = {
  user: AuthUser | null;
  ready: boolean;
  refresh: () => Promise<void>;
  setSessionUser: (user: AuthUser | null) => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      return;
    }
    try {
      const data = await api<{ user: AuthUser }>(env.paths.authMe);
      setUser(data.user);
    } catch (err) {
      // Only clear session on real auth failure. status 0 = network blip /
      // backend restart (node --watch) — keep the token and retry later.
      if (err instanceof ApiError && err.status === 401) {
        setAccessToken(null);
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api(env.paths.authLogout, { method: "POST" });
    } catch {
      /* clear local session anyway */
    }
    setAccessToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      ready,
      refresh,
      setSessionUser: setUser,
      logout,
    }),
    [user, ready, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
